import React, { useEffect, useState } from 'react';
import { useScopedI18n } from '@locales/client';

import { useIsAuth } from '@/shared/providers/user-provider/use-user';
import { Condition } from '@/shared/ui/condition/condition';
import { useBrandTheme } from '@/shared/hooks/useBrandTheme';

import { useLoyaltyList } from '@/flat-pages/status/model/hooks';
import {
  StatusCardStyle,
  ActiveBg,
  StatusCardContent,
  StatusCardCrown,
  StatusCardTitle,
  DetailField,
  DetailLabel,
  DetailValue,
  ProgressBar,
  ProgressBarLabel,
} from './styles';

export const CROWN_IMAGE_URL = '/images/icons/status_new/status_level_';

type StatusCardT = {
  cardLevel: number;
};

export const StatusCard = ({ cardLevel }: StatusCardT) => {
  const isAuth = useIsAuth();
  const { isPh } = useBrandTheme();
  const tier = isPh ? Math.floor(cardLevel / 2) : cardLevel;
  const subIndex = cardLevel % 2;
  const [cashback, setCashback] = useState(0);
  const [points, setPoints] = useState(0);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [progressOfLevel, setProgressOfLevel] = useState(0);
  const [progress, setProgress] = useState(0);
  const [targetPoints, setTargetPoints] = useState(0);
  const { isError, loyaltyList, isLoading } = useLoyaltyList();

  const s = useScopedI18n('status');
  const st = useScopedI18n('status.status_name');

  useEffect(() => {
    if (!isLoading && !isError && loyaltyList) {
      let currentLevelIndex = loyaltyList?.currentLevel - 1;
      const cardLevelData = loyaltyList.levels[cardLevel];

      setCashback(cardLevelData?.cashback);
      setPoints(cardLevelData?.points);

      if (isAuth) {
        setCurrentLevel(loyaltyList?.currentLevel);

        setProgressOfLevel(loyaltyList?.loyalty || 0);
        const nextNeedLoyalty =
          loyaltyList?.levels[currentLevelIndex + 1]?.needLoyalty;
        const currentProgress =
          (100 * loyaltyList?.loyalty) /
          (nextNeedLoyalty -
            loyaltyList?.levels[currentLevelIndex]?.needLoyalty);
        setProgress(currentProgress);

        setTargetPoints(nextNeedLoyalty || 0);
      }
    } else if (isError) {
      console.warn(isError);
    }
  }, [isLoading, loyaltyList, cardLevel, isError, isAuth]);

  let minRebate = Infinity;
  let maxRebate = -Infinity;
  if (isPh) {
    for (const rate of Object.values(
      (loyaltyList?.levels?.[cardLevel]?.rebate ?? {}) as Record<string, number>
    )) {
      minRebate = Math.min(minRebate, rate);
      maxRebate = Math.max(maxRebate, rate);
    }
  }
  if (minRebate === Infinity) {
    minRebate = 0;
    maxRebate = 0;
  }

  const matchLevel = {
    isActive: isAuth && cardLevel == currentLevel - 1,
    isCompleted:
      isAuth &&
      cardLevel == currentLevel &&
      loyaltyList != undefined &&
      !isError &&
      !isLoading,
  };

  return (
    <StatusCardStyle $level={tier}>
      <Condition match={matchLevel.isActive}>
        <ActiveBg $level={tier} />
      </Condition>
      <StatusCardContent>
        <StatusCardTitle $isActive={matchLevel.isActive}>
          <StatusCardCrown
            data-image-url={`${CROWN_IMAGE_URL}${tier}${isPh ? '_ph' : ''}.png`}
          ></StatusCardCrown>
          {isPh
            ? `${st(String(tier))} ${subIndex === 0 ? 'I' : 'II'}`
            : st(String(cardLevel))}
        </StatusCardTitle>
        <Condition match={matchLevel.isCompleted}>
          <ProgressBar $progress={progress}>
            <ProgressBarLabel>
              {s('progress_value', {
                value: Math.trunc(progressOfLevel),
                total: targetPoints,
              })}
            </ProgressBarLabel>
          </ProgressBar>
        </Condition>
      </StatusCardContent>
      <DetailField $isActive={matchLevel.isActive}>
        <DetailLabel>{s('points')}</DetailLabel>
        <DetailValue>×{points}</DetailValue>
      </DetailField>
      <DetailField $isActive={matchLevel.isActive}>
        <DetailLabel>{isPh ? s('rebate' as never) : s('cashback')}</DetailLabel>
        <DetailValue $compact={isPh}>
          {isPh
            ? minRebate === maxRebate
              ? `${minRebate}%`
              : `${minRebate}% - ${maxRebate}%`
            : `${cashback}%`}
        </DetailValue>
      </DetailField>
    </StatusCardStyle>
  );
};
